/* global Word console */
import { CITATION_COMPONENT_REGEX, CITATION_SPAN_REGEX } from "./regex";
import { extractTitleForMatchPrepared, PreparedParagraphTitleData } from "./titleExtractor";
import { CitationType, AnalyzeCitationEntry } from "../models/benchly.models";
import { wordApiColors } from "../theme/benchly-theme";

const CITATION_TAG = "legalCitation";
const LINK_COLOR = wordApiColors.hyperlinkBlue;
const TO_SKIP = new Set([" ", "v", "v.", "vs", "vs."]);
const SEPARATOR_CHARS = new Set([" ", "\u00A0", "\r", "\f", "\t"]);
const SEPARATOR_LIST = [" ", "\u00A0", "\r", "\f", "\t"];
const BOOKMARK_PREFIX = "ba_cite_";
export const CITATION_BOOKMARK_NS = "http://schemas.beyondassured.com/citation-bookmarks";

/**
 * Extracts the last page number from a page range string.
 * Examples:
 *   "242" → "242"
 *   "226, 242" → "242"
 *   "226-242" → "242"
 *   "100, 110-115" → "115"
 */
function extractLastPage(pages: string | undefined): string | undefined {
  if (!pages) return undefined;

  // Split by comma and take the last segment
  const segments = pages.split(",");
  const lastSegment = segments[segments.length - 1].trim();

  // If the last segment has a dash/range (e.g., "110-115"), take the last number
  const rangeMatch = lastSegment.match(/(\d+)\s*$/);
  return rangeMatch ? rangeMatch[1] : undefined;
}

function citationTypeToString(ct: CitationType): string {
  return ct === CitationType.ParallelCites ? "ParallelCites" : "SingleCite";
}

/** Metadata for a single citation bookmark, stored in a CustomXmlPart. */
export interface CitationBookmarkEntry {
  bookmarkName: string;
  pattern: string;
  text: string;
  title: string;
  identifierText?: string;
  apiIdentifier?: string; // Volume + Reporter + Last Page (for API)
  year?: string;
  pages?: string; // Full page range
  // NEW: what the API actually needs
  citationType: CitationType;
  citations: AnalyzeCitationEntry[];
}

/*type CitationMatch = {
  paraIndex: number;
  text: string;
  pattern: string;
  startInPara: number;
  endInPara: number;
  globalStart: number;
  year?: string;
  identifierText?: string;
  apiIdentifier?: string; // Volume + Reporter + Last Page (for API)
  volume?: string;
  reporter?: string;
  pages?: string; // Full page range (e.g., "226, 242")
  dateArea?: string; // Full date area including parentheses
};*/

type ParagraphInfo = {
  paragraph: Word.Paragraph;
  text: string;
  startPos: number;
  endPos: number;
};

export type ProgressCallback = (progress: number, message?: string) => void;

/**
 * OPTIMIZED scanner that minimizes context.sync() calls by batching operations.
 * Previous version: ~1 sync per match (N matches = N syncs)
 * Optimized version: ~3-4 syncs total regardless of match count
 */
export async function searchAndMarkMatches(onProgress?: ProgressCallback): Promise<number> {
  let totalMatches = 0;

  const timings: Record<string, number> = {};
  const scannerStart = performance.now();

  const logTiming = (label: string, startTime: number) => {
    const duration = performance.now() - startTime;
    timings[label] = duration;
    console.log(`  ⏱️ [Scanner: ${label}] ${duration.toFixed(2)}ms`);
  };

  try {
    await Word.run(async (context) => {
      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 1: Load document structure (2 syncs)
      // ═══════════════════════════════════════════════════════════════════════
      const phase1Start = performance.now();
      onProgress?.(0.05, "Loading document...");

      const body = context.document.body;
      const footnotes = context.document.body.footnotes;
      const existingControls = context.document.contentControls.getByTag(CITATION_TAG);
      const existingBookmarkParts =
        context.document.customXmlParts.getByNamespace(CITATION_BOOKMARK_NS);

      // Batch load: footnotes + existing controls + existing bookmark metadata
      footnotes.load("items");
      existingControls.load("items");
      existingBookmarkParts.load("items");
      await context.sync(); // SYNC #1

      onProgress?.(0.1, "Removing existing citation markers...");

      // Remove existing content controls (legacy)
      console.log(
        `Removing ${existingControls.items.length} existing "${CITATION_TAG}" content controls`
      );
      for (const control of existingControls.items) {
        control.delete(true);
      }

      // Remove existing bookmark metadata + delete bookmarks
      if (existingBookmarkParts.items.length > 0) {
        const xmlPart = existingBookmarkParts.items[0];
        const xmlNode = xmlPart.getXml();
        await context.sync();
        const oldEntries = parseCitationBookmarkXml(xmlNode.value);
        for (const entry of oldEntries) {
          context.document.deleteBookmark(entry.bookmarkName);
        }
        for (const part of existingBookmarkParts.items) {
          part.delete();
        }
      }

      // Load body paragraphs + footnote paragraphs in one batch
      const paragraphs = body.paragraphs;
      paragraphs.load("text,items");

      const footnoteParagraphCollections: Word.ParagraphCollection[] = [];
      for (const footnote of footnotes.items) {
        const fnParagraphs = footnote.body.paragraphs;
        fnParagraphs.load("items,text");
        footnoteParagraphCollections.push(fnParagraphs);
      }

      await context.sync(); // SYNC #2: delete controls + load all paragraphs

      logTiming("Phase 1: Load document structure", phase1Start);

      onProgress?.(0.15, "Building paragraph map...");

      // Build paragraph map (pure JS, no sync needed)
      const allParagraphs: Word.Paragraph[] = [...paragraphs.items];
      for (const fnParagraphs of footnoteParagraphCollections) {
        allParagraphs.push(...fnParagraphs.items);
      }

      let currentPos = 0;
      const paragraphMap: ParagraphInfo[] = allParagraphs.map((para) => {
        const text = para.text;
        const info = {
          paragraph: para,
          text: text,
          startPos: currentPos,
          endPos: currentPos + text.length,
        };
        currentPos += text.length;
        return info;
      });

      console.log(`Total document text length: ${currentPos} characters`);

      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 2: Find all matches using pure JS regex (no sync needed)
      // ═══════════════════════════════════════════════════════════════════════
      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 2: Find spans (single vs parallel) + enrich with component groups
      // ═══════════════════════════════════════════════════════════════════════
      const phase2Start = performance.now();
      onProgress?.(0.2, "Searching for citations...");

      type SpanHit = {
        paraIndex: number;
        startInPara: number;
        endInPara: number;
        text: string;
        isParallel: boolean;
      };

      type EnrichedHit = {
        paraIndex: number;
        startInPara: number;
        endInPara: number;
        globalStart: number;
        text: string; // span text
        citationType: CitationType;
        entries: AnalyzeCitationEntry[];
      };

      const enrichedHits: EnrichedHit[] = [];
      const detectParallelByComponents = (spanText: string): boolean => {
        // Count component citations inside the span.
        // If > 1, treat span as parallel.
        CITATION_COMPONENT_REGEX.lastIndex = 0;

        let count = 0;
        let m: RegExpExecArray | null;

        while ((m = CITATION_COMPONENT_REGEX.exec(spanText)) !== null) {
          if (!m[0] || m[0].length === 0) {
            CITATION_COMPONENT_REGEX.lastIndex++;
            continue;
          }
          count++;
          if (count > 1) return true;
        }

        return false;
      };
      for (let i = 0; i < paragraphMap.length; i++) {
        const paraInfo = paragraphMap[i];
        const paraText = paraInfo.text;

        // 1) Run span regex once
        CITATION_SPAN_REGEX.lastIndex = 0;
        const spanHits: SpanHit[] = [];
        let spanMatch: RegExpExecArray | null;

        while ((spanMatch = CITATION_SPAN_REGEX.exec(paraText)) !== null) {
          if (!spanMatch[0] || spanMatch[0].length === 0) {
            CITATION_SPAN_REGEX.lastIndex++;
            continue;
          }

          const isParallel = detectParallelByComponents(spanMatch[0]);
          console.log(`Citation Type: ${isParallel ? "parallel" : "single"}`, spanMatch[0]);
          spanHits.push({
            paraIndex: i,
            startInPara: spanMatch.index,
            endInPara: spanMatch.index + spanMatch[0].length,
            text: spanMatch[0],
            isParallel: isParallel,
          });
        }

        if (spanHits.length === 0) continue;

        // 2) Collect parallel ranges
        const parallelRanges = spanHits
          .filter((h) => h.isParallel)
          .map((h) => ({ start: h.startInPara, end: h.endInPara }));

        const isContainedInParallel = (start: number, end: number): boolean => {
          for (const pr of parallelRanges) {
            if (start >= pr.start && end <= pr.end) return true;
          }
          return false;
        };

        // 3) Decide which spans to keep:
        //    - keep ALL parallels
        //    - keep singles only if NOT contained within any parallel range
        const keptSpans = spanHits.filter(
          (h) => h.isParallel || !isContainedInParallel(h.startInPara, h.endInPara)
        );

        // 4) For each kept span, run component regex on the span text to produce entries
        for (const span of keptSpans) {
          const spanText = span.text;

          CITATION_COMPONENT_REGEX.lastIndex = 0;
          const entries: AnalyzeCitationEntry[] = [];
          let componentMatch: RegExpExecArray | null;

          while ((componentMatch = CITATION_COMPONENT_REGEX.exec(spanText)) !== null) {
            if (!componentMatch[0] || componentMatch[0].length === 0) {
              CITATION_COMPONENT_REGEX.lastIndex++;
              continue;
            }

            const cg = componentMatch.groups || {};
            const volume = cg.Volume?.trim();
            const reporter = cg.Reporter?.trim();
            const pages = cg.Pages?.trim();
            const dateArea = cg.DateArea;

            // Year extraction: prefer DateArea named group, fallback to "(YYYY)" in component text
            let year = "";
            if (dateArea) {
              year = dateArea.replace(/[()]/g, "");
            } else {
              const ym = componentMatch[0].match(/\((\d{4})\)/);
              year = ym ? ym[1] : "";
            }

            // Build "identifierOnDocument" for API:
            // Prefer Volume+Reporter+LastPage when available, else the raw component match trimmed.
            let identifierOnDocument = componentMatch[0].trim();

            if (volume && reporter && pages) {
              const lastPage = extractLastPage(pages);
              if (lastPage) {
                identifierOnDocument = `${volume} ${reporter} ${lastPage}`.trim();
              }
            }

            entries.push({
              identifierOnDocument,
              titleOnDocument: undefined, // filled later from extracted title at span-level
              yearOnDocument: year || undefined,
            });
          }

          // If for some reason component regex yielded nothing, skip.
          // (You can choose to fall back to the span text as a single entry, but that’s usually worse.)
          if (entries.length === 0) continue;

          const citationType = span.isParallel
            ? CitationType.ParallelCites
            : CitationType.SingleCite;

          enrichedHits.push({
            paraIndex: i,
            startInPara: span.startInPara,
            endInPara: span.endInPara,
            globalStart: paraInfo.startPos + span.startInPara,
            text: spanText,
            citationType,
            entries,
          });
        }
      }

      // Sort and remove overlaps at the SPAN level (keep earliest, prefer longer)
      enrichedHits.sort((a, b) => {
        if (a.globalStart !== b.globalStart) return a.globalStart - b.globalStart;
        return b.text.length - a.text.length;
      });

      const nonOverlappingHits: EnrichedHit[] = [];
      let lastSpanEnd = -1;

      for (const hit of enrichedHits) {
        const hitEnd = hit.globalStart + hit.text.length;
        if (hit.globalStart >= lastSpanEnd) {
          nonOverlappingHits.push(hit);
          lastSpanEnd = hitEnd;
        }
      }

      console.log(`Found ${nonOverlappingHits.length} citation spans (after overlap removal)`);
      logTiming("Phase 2: Span+Component matching (pure JS)", phase2Start);

      onProgress?.(0.3, `Found ${nonOverlappingHits.length} potential citations...`);

      if (nonOverlappingHits.length === 0) {
        onProgress?.(1.0, "Scan complete! No citations found.");
        return;
      }

      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 3: Prepare title data for all paragraphs with matches (1 sync)
      // ═══════════════════════════════════════════════════════════════════════
      const phase3Start = performance.now();
      onProgress?.(0.35, "Preparing title extraction data...");

      // Group spans by paragraph
      const matchesByParagraph = new Map<number, EnrichedHit[]>();
      for (const hit of nonOverlappingHits) {
        const list = matchesByParagraph.get(hit.paraIndex) ?? [];
        list.push(hit);
        matchesByParagraph.set(hit.paraIndex, list);
      }

      // Batch load: get text ranges for ALL paragraphs with matches.
      // These Range objects serve dual purpose:
      //   1) Title extraction (italic/underline detection)
      //   2) Constructing match ranges via expandTo() — eliminates the search phase entirely
      const paragraphRangeData: Array<{
        paraIndex: number;
        paraInfo: ParagraphInfo;
        wordRanges: Word.RangeCollection;
      }> = [];

      for (const [paraIndex] of matchesByParagraph.entries()) {
        const paraInfo = paragraphMap[paraIndex];
        const paragraphRange = paraInfo.paragraph.getRange();
        const wordRanges = paragraphRange.getTextRanges(SEPARATOR_LIST, true);
        wordRanges.load("items/text,items/font/italic,items/font/underline");
        paragraphRangeData.push({ paraIndex, paraInfo, wordRanges });
      }

      await context.sync(); // SYNC #3: load all word ranges

      // Process title data AND collect token Range objects (pure JS after sync)
      const preparedTitleDataMap = new Map<number, PreparedParagraphTitleData>();
      const tokenRangesByPara = new Map<number, Word.Range[]>();
      for (const { paraIndex, paraInfo, wordRanges } of paragraphRangeData) {
        const { titleData, tokenRanges } = buildPreparedTitleDataFromLoadedRanges(
          wordRanges,
          paraInfo.text
        );
        preparedTitleDataMap.set(paraIndex, titleData);
        tokenRangesByPara.set(paraIndex, tokenRanges);
      }

      logTiming("Phase 3: Prepare title + range data (batched)", phase3Start);

      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 4: Apply font styling + insert bookmarks (no content controls)
      // Bookmarks are ~100x faster than insertContentControl().
      // Metadata is stored in a CustomXmlPart keyed by bookmark name.
      // ═══════════════════════════════════════════════════════════════════════
      const phase4Start = performance.now();
      onProgress?.(0.5, `Marking ${nonOverlappingHits.length} citations...`);

      const bookmarkEntries: CitationBookmarkEntry[] = [];

      for (const [paraIndex, matches] of matchesByParagraph.entries()) {
        const titleData = preparedTitleDataMap.get(paraIndex)!;
        const ranges = tokenRangesByPara.get(paraIndex)!;
        const tokens = titleData.tokens;

        if (tokens.length === 0) continue;

        for (const match of matches) {
          // Binary search: find the first token with endIndex > match.startInPara
          let lo = 0,
            hi = tokens.length - 1;
          let firstTokenIdx = -1;
          while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            if (tokens[mid].endIndex > match.startInPara) {
              firstTokenIdx = mid;
              hi = mid - 1;
            } else {
              lo = mid + 1;
            }
          }

          if (firstTokenIdx < 0) continue;

          // Scan forward to find last token that overlaps with the match
          let lastTokenIdx = firstTokenIdx;
          while (
            lastTokenIdx + 1 < tokens.length &&
            tokens[lastTokenIdx + 1].startIndex < match.endInPara
          ) {
            lastTokenIdx++;
          }

          // Construct match range from token Range objects
          const matchRange =
            firstTokenIdx === lastTokenIdx
              ? ranges[firstTokenIdx]
              : ranges[firstTokenIdx].expandTo(ranges[lastTokenIdx]);

          // Apply font styling only (very fast, no CC overhead)
          matchRange.font.set({
            color: LINK_COLOR,
            underline: Word.UnderlineType.single,
          });

          // Insert a bookmark to anchor this citation in the document
          const bookmarkName = `${BOOKMARK_PREFIX}${totalMatches}`;
          matchRange.insertBookmark(bookmarkName);

          // Extract title and collect metadata (pure JS)
          const title = extractTitleForMatchPrepared(match.startInPara, titleData);
          // Apply title to each entry (span-level title is the same for all components)
          const citationsWithTitle: AnalyzeCitationEntry[] = match.entries.map((e) => ({
            ...e,
            titleOnDocument: title || undefined,
          }));

          bookmarkEntries.push({
            bookmarkName,
            pattern: "span", // or keep whatever string you prefer
            text: match.text,
            title,
            citationType: match.citationType,
            citations: citationsWithTitle,
            // Optional legacy/debug fields:
            identifierText: citationsWithTitle[0]?.identifierOnDocument,
            apiIdentifier: citationsWithTitle[0]?.identifierOnDocument,
            year: citationsWithTitle[0]?.yearOnDocument,
          });

          totalMatches++;
        }
      }

      await context.sync(); // SYNC #4: apply all font changes + bookmarks

      logTiming("Phase 4: Apply font + bookmarks", phase4Start);

      // ═══════════════════════════════════════════════════════════════════════
      // PHASE 5: Store bookmark metadata in CustomXmlPart
      // ═══════════════════════════════════════════════════════════════════════
      const phase5Start = performance.now();

      // Remove existing bookmark metadata XML part
      const existingParts = context.document.customXmlParts.getByNamespace(CITATION_BOOKMARK_NS);
      existingParts.load("items");
      await context.sync();
      for (const part of existingParts.items) {
        part.delete();
      }

      // Build XML with bookmark metadata
      const xmlEntries = bookmarkEntries
        .map((e) => {
          // Store citations as JSON in an attribute (escaped for XML)
          const citationsJson = JSON.stringify(e.citations ?? []);
          return `<c bk="${escapeXmlAttr(e.bookmarkName)}" p="${escapeXmlAttr(e.pattern)}" t="${escapeXmlAttr(e.text)}" tl="${escapeXmlAttr(e.title)}" ct="${escapeXmlAttr(citationTypeToString(e.citationType))}" cits="${escapeXmlAttr(citationsJson)}" id="${escapeXmlAttr(e.identifierText ?? "")}" api="${escapeXmlAttr(e.apiIdentifier ?? "")}" y="${escapeXmlAttr(e.year ?? "")}" pg="${escapeXmlAttr(e.pages ?? "")}"/>`;
        })
        .join("");
      const xml = `<?xml version="1.0" encoding="UTF-8"?><CitationBookmarks xmlns="${CITATION_BOOKMARK_NS}">${xmlEntries}</CitationBookmarks>`;
      context.document.customXmlParts.add(xml);
      await context.sync(); // SYNC #5: store metadata

      logTiming("Phase 5: Store bookmark metadata (XML)", phase5Start);

      onProgress?.(1.0, `Scan complete! Found ${totalMatches} citations.`);
      console.log(`Created ${totalMatches} content controls`);

      // Print scanner timing summary
      const scannerDuration = performance.now() - scannerStart;
      console.log("\n  📊 ─── SCANNER TIMING SUMMARY ───");
      Object.entries(timings).forEach(([label, duration]) => {
        const pct = ((duration / scannerDuration) * 100).toFixed(1);
        console.log(`  📊 ${pct.padStart(5)}% | ${duration.toFixed(0).padStart(5)}ms | ${label}`);
      });
      console.log(
        `  📊 TOTAL: ${scannerDuration.toFixed(0)}ms (${(scannerDuration / 1000).toFixed(2)}s)`
      );
      console.log("  📊 ─────────────────────────────\n");
    });
  } catch (error) {
    console.log("Error in searchAndMarkMatches:", error);
    throw error;
  }

  return totalMatches;
}

/**
 * Builds PreparedParagraphTitleData from already-loaded word ranges.
 * This avoids an additional context.sync() by using pre-loaded data.
 */
function buildPreparedTitleDataFromLoadedRanges(
  wordRanges: Word.RangeCollection,
  paragraphText: string
): { titleData: PreparedParagraphTitleData; tokenRanges: Word.Range[] } {
  const textTokenPositions: { startIndex: number; endIndex: number }[] = [];

  let i = 0;
  while (i < paragraphText.length) {
    while (i < paragraphText.length && SEPARATOR_CHARS.has(paragraphText[i])) i++;
    if (i >= paragraphText.length) break;

    const tokenStart = i;
    while (i < paragraphText.length && !SEPARATOR_CHARS.has(paragraphText[i])) i++;
    const tokenEnd = i;

    if (tokenEnd > tokenStart) {
      textTokenPositions.push({ startIndex: tokenStart, endIndex: tokenEnd });
    }
  }

  const tokens: PreparedToken[] = [];
  const tokenRanges: Word.Range[] = [];
  let textTokenIndex = 0;

  for (let rangeIndex = 0; rangeIndex < wordRanges.items.length; rangeIndex++) {
    const rangeText = wordRanges.items[rangeIndex].text || "";
    if (!rangeText) continue;

    if (textTokenIndex >= textTokenPositions.length) break;
    const tokenPos = textTokenPositions[textTokenIndex++];

    const trimmedTokenText = rangeText.trim();
    const trimmedTokenLowercase = trimmedTokenText.toLowerCase();

    const italicValue = wordRanges.items[rangeIndex].font.italic as boolean | null | undefined;
    const isTokenItalic = italicValue === true || italicValue === null;

    const underlineValue = wordRanges.items[rangeIndex].font.underline as
      | Word.UnderlineType
      | "Mixed"
      | "None"
      | null
      | undefined;

    const isTokenUnderlined =
      underlineValue === "Mixed" || (underlineValue != null && underlineValue !== "None");

    const shouldSkipToken = trimmedTokenText === "" || TO_SKIP.has(trimmedTokenLowercase);

    tokens.push({
      text: rangeText,
      startIndex: tokenPos.startIndex,
      endIndex: tokenPos.endIndex,
      isItalic: isTokenItalic,
      isUnderlined: isTokenUnderlined,
      trimmedLowercase: trimmedTokenLowercase,
      shouldSkipToken,
    });
    tokenRanges.push(wordRanges.items[rangeIndex]);
  }

  return { titleData: { tokens }, tokenRanges };
}

type PreparedToken = {
  text: string;
  startIndex: number;
  endIndex: number;
  isItalic: boolean;
  isUnderlined: boolean;
  trimmedLowercase: string;
  shouldSkipToken: boolean;
};

export async function printAllMatches(): Promise<void> {
  try {
    await Word.run(async (context) => {
      const contentControls = context.document.contentControls;

      // Load all content controls with their properties
      context.load(contentControls, ["tag", "title", "text"]);
      await context.sync();

      console.log(`\n=== Found ${contentControls.items.length} Content Controls ===`);

      // Group matches by pattern type
      const matchesByPattern = new Map<string, Array<string>>();

      for (const cc of contentControls.items) {
        const patternName = cc.tag || "unknown";
        const matchText = cc.text;

        if (!matchesByPattern.has(patternName)) {
          matchesByPattern.set(patternName, []);
        }
        matchesByPattern.get(patternName)!.push(matchText);
      }

      // Print grouped results
      for (const patternName of Array.from(matchesByPattern.keys())) {
        const matches = matchesByPattern.get(patternName)!;
        console.log(`\n[${patternName.toUpperCase()}] - ${matches.length} matches:`);

        // Get unique matches for this pattern
        const uniqueMatches = Array.from(new Set(matches));
        uniqueMatches.forEach((match, index) => {
          const count = matches.filter((m) => m === match).length;
          console.log(`  ${index + 1}. "${match}" (${count}x)`);
        });
      }

      console.log("\n=== End of Matches ===\n");
    });
  } catch (error) {
    console.log("Error in printAllMatches:", error);
    throw error;
  }
}

/**
 * OPTIMIZED: Clears all citation markers from the document.
 * Reduced from 3 syncs to 2 syncs by batching operations.
 */
export async function resetMatches(): Promise<number> {
  return await Word.run(async (context) => {
    // Read bookmark names from XML metadata
    const parts = context.document.customXmlParts.getByNamespace(CITATION_BOOKMARK_NS);
    parts.load("items");
    await context.sync(); // SYNC #1

    if (parts.items.length === 0) return 0;

    const xmlPart = parts.items[0];
    const xmlNode = xmlPart.getXml();
    await context.sync(); // SYNC #2: get XML

    const entries = parseCitationBookmarkXml(xmlNode.value);
    if (entries.length === 0) {
      xmlPart.delete();
      await context.sync();
      return 0;
    }

    // Batch: load all bookmark ranges in one sync
    const rangeMap = new Map<string, Word.Range>();
    for (const entry of entries) {
      const range = context.document.getBookmarkRangeOrNullObject(entry.bookmarkName);
      range.load("isNullObject");
      rangeMap.set(entry.bookmarkName, range);
    }
    await context.sync(); // SYNC #3: load all bookmark ranges

    // Apply reset using the already-loaded ranges, skipping missing bookmarks
    for (const entry of entries) {
      const range = rangeMap.get(entry.bookmarkName)!;
      if (range.isNullObject) {
        console.warn(`Bookmark "${entry.bookmarkName}" not found, skipping reset.`);
        continue;
      }
      // Reset formatting
      range.font.color = wordApiColors.textPrimary;
      range.font.underline = Word.UnderlineType.none;
      // Clear hyperlink
      try {
        (range as any).hyperlink = "";
      } catch {
        // Ignore hyperlink clear errors
      }
      // Delete the bookmark
      context.document.deleteBookmark(entry.bookmarkName);
    }

    // Delete the XML metadata part
    xmlPart.delete();

    // Also clean up any legacy content controls
    const legacyControls = context.document.contentControls.getByTag(CITATION_TAG);
    legacyControls.load("items");
    await context.sync(); // SYNC #4: apply resets + load legacy
    for (const control of legacyControls.items) {
      control.delete(true);
    }
    if (legacyControls.items.length > 0) {
      await context.sync();
    }

    return entries.length;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// XML helpers for bookmark metadata
// ─────────────────────────────────────────────────────────────────────────────

export function escapeXmlAttr(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function unescapeXmlAttr(str: string): string {
  return str
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

export function parseCitationBookmarkXml(xml: string): SavedMatch[] {
  const results: SavedMatch[] = [];

  // NEW format:
  // <c bk=".." p=".." t=".." tl=".." ct="SingleCite|ParallelCites" cits="[json]" id=".." api=".." y=".." pg=".."/>
  const newRegex =
    /<c\s+bk="([^"]*?)"\s+p="([^"]*?)"\s+t="([^"]*?)"\s+tl="([^"]*?)"\s+ct="([^"]*?)"\s+cits="([^"]*?)"\s+id="([^"]*?)"\s+api="([^"]*?)"\s+y="([^"]*?)"\s+pg="([^"]*?)"\s*\/>/g;

  // LEGACY format (older scans / older savePending writer):
  // <c bk=".." p=".." t=".." tl=".." id=".." api=".." y=".." pg=".."/>
  const legacyRegex =
    /<c\s+bk="([^"]*?)"\s+p="([^"]*?)"\s+t="([^"]*?)"\s+tl="([^"]*?)"\s+id="([^"]*?)"\s+api="([^"]*?)"\s+y="([^"]*?)"\s+pg="([^"]*?)"\s*\/>/g;

  // Pass 1: new format
  let m: RegExpExecArray | null;
  while ((m = newRegex.exec(xml)) !== null) {
    const bookmarkName = unescapeXmlAttr(m[1]);
    const pattern = unescapeXmlAttr(m[2]);
    const text = unescapeXmlAttr(m[3]);
    const title = unescapeXmlAttr(m[4]);

    const citationTypeStr = unescapeXmlAttr(m[5]);
    const citationsJson = unescapeXmlAttr(m[6]);

    let citations: AnalyzeCitationEntry[] = [];
    try {
      const parsed = JSON.parse(citationsJson);
      if (Array.isArray(parsed)) citations = parsed;
    } catch {
      citations = [];
    }

    const identifierText = unescapeXmlAttr(m[7]) || undefined;
    const apiIdentifier = unescapeXmlAttr(m[8]) || undefined;
    const year = unescapeXmlAttr(m[9]) || undefined;
    const pages = unescapeXmlAttr(m[10]) || undefined;

    if (!citations || citations.length === 0) {
      const fallbackIdentifier = apiIdentifier || identifierText || text || "";
      citations = [
        {
          identifierOnDocument: fallbackIdentifier,
          titleOnDocument: title || undefined,
          yearOnDocument: year || undefined,
        },
      ];
    }

    const citationTypeNormalized = citationTypeStr.trim();
    const citationType: CitationType =
      citationTypeNormalized === "ParallelCites" || citationTypeNormalized === "1"
        ? CitationType.ParallelCites
        : CitationType.SingleCite;

    results.push({
      bookmarkName,
      pattern,
      text,
      title,
      citationType,
      citations,
      identifierText,
      apiIdentifier,
      year,
      pages,
    });
  }

  // If we successfully parsed new-format entries, return them
  if (results.length > 0) return results;

  // Pass 2: legacy format
  while ((m = legacyRegex.exec(xml)) !== null) {
    const bookmarkName = unescapeXmlAttr(m[1]);
    const pattern = unescapeXmlAttr(m[2]);
    const text = unescapeXmlAttr(m[3]);
    const title = unescapeXmlAttr(m[4]);

    const identifierText = unescapeXmlAttr(m[5]) || undefined;
    const apiIdentifier = unescapeXmlAttr(m[6]) || undefined;
    const year = unescapeXmlAttr(m[7]) || undefined;
    const pages = unescapeXmlAttr(m[8]) || undefined;

    // Legacy has no way to know parallel vs single — treat as SingleCite with one entry.
    const fallbackIdentifier = apiIdentifier || identifierText || text || "";
    const citations: AnalyzeCitationEntry[] = [
      {
        identifierOnDocument: fallbackIdentifier,
        titleOnDocument: title || undefined,
        yearOnDocument: year || undefined,
      },
    ];

    results.push({
      bookmarkName,
      pattern,
      text,
      title,
      citationType: CitationType.SingleCite,
      citations,
      identifierText,
      apiIdentifier,
      year,
      pages,
    });
  }

  return results;
}

export interface SavedMatch {
  pattern: string;
  text: string; // Original citation text
  title: string;
  bookmarkName: string;
  identifierText?: string; // Citation without year
  apiIdentifier?: string; // Volume + Reporter + Last Page (for API)
  year?: string;
  pages?: string; // Full page range
  citationType: CitationType;
  citations: AnalyzeCitationEntry[];
  // Legacy fields for backwards compatibility (if needed)
  isParallel?: boolean;
  parallelGroupId?: string;
  parallelCitations?: Array<{
    identifierText: string;
    apiIdentifier?: string;
    year?: string;
  }>;
}

export async function getSavedMatches(): Promise<SavedMatch[]> {
  try {
    return await Word.run(async (context) => {
      // Read citation metadata from CustomXmlPart (no CC traversal)
      const parts = context.document.customXmlParts.getByNamespace(CITATION_BOOKMARK_NS);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) {
        console.log("No citation bookmark XML part found");
        return [];
      }

      const xmlPart = parts.items[0];
      const xmlNode = xmlPart.getXml();
      await context.sync();

      const savedMatches = parseCitationBookmarkXml(xmlNode.value);
      console.log(`Found ${savedMatches.length} saved matches (from bookmark XML)`);
      return savedMatches;
    });
  } catch (error) {
    console.log("Error in getSavedMatches:", error);
    throw error;
  }
}
